// Reduction primitives — mirrors @webpic/reductions/src/types.ts and
// pypic.reductions (verb is `reduce`, not `project`, to avoid colliding
// with THREE Vector3.project). The Literal covers all 10 pypic ops; only
// the subset in SUPPORTED_OPS is implemented in Stage 6.

import type { SelectionId } from '@store/schema';

/**
 * All 10 reductions pypic ships with. magviz Stage 6 implements 4 of these;
 * the rest are accepted in the wire/store shape but the compute dispatcher
 * returns `{ kind: 'unsupported' }` until a future stage adds them. Keep
 * the order matching pypic.reductions.Reduction.
 */
export type Reduction =
    | 'integrate'
    | 'sum'
    | 'mean'
    | 'median'
    | 'max'
    | 'min'
    | 'std'
    | 'var'
    | 'argmax'
    | 'argmin';

export const SUPPORTED_OPS = ['mean', 'sum', 'max', 'min'] as const satisfies readonly Reduction[];
export type SupportedOp = (typeof SUPPORTED_OPS)[number];

export function isSupported(op: Reduction): op is SupportedOp {
    return (SUPPORTED_OPS as readonly Reduction[]).includes(op);
}

/** Axis labels — magviz works in Cartesian only today. */
export type AxisLabel = 'x' | 'y' | 'z';
export const AXIS_LABELS = ['x', 'y', 'z'] as const satisfies readonly AxisLabel[];

/**
 * Wire/store shape for a reduction request. Axes are the dimensions to
 * *reduce over* (matches pypic's `axis` semantics). domain references a
 * SelectionId from the registry or 'full' for no scoping.
 */
export interface ReductionSpec {
    readonly field: string;
    readonly op: Reduction;
    readonly axes: ReadonlyArray<AxisLabel>;
    readonly domain: SelectionId | 'full';
}

/**
 * Discriminated result of a reduce() call.
 *
 * - `scalar`: 0-D output (all 3 axes reduced).
 * - `profile`: 1-D output (exactly 1 surviving axis); `coords` are physical
 *   positions along that axis (cell centers), `values` are the reduction.
 * - `unsupported`: compute path declined the op (either not in SUPPORTED_OPS
 *   or the surviving-axis count is ≥2, i.e. 2-D heatmap deferred to v2).
 * - `empty`: the domain selected zero cells (sphere outside the grid, box
 *   with degenerate bounds, etc.). Distinct from a NaN result.
 */
export type ReductionResult =
    | {
          readonly kind: 'scalar';
          readonly op: SupportedOp;
          readonly value: number;
          readonly count: number;
      }
    | {
          readonly kind: 'profile';
          readonly op: SupportedOp;
          readonly axis: AxisLabel;
          readonly coords: Float32Array;
          readonly values: Float32Array;
          readonly counts: Int32Array;
      }
    | { readonly kind: 'unsupported'; readonly op: Reduction; readonly reason: string }
    | { readonly kind: 'empty' };
